import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  CircleDollarSign,
  Play,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import { AudienceFilters, AudienceSummary } from '../components/AudienceFilters';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Drawer } from '../components/Drawer';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorNote,
  Field,
  IdChip,
  Input,
  Pagination,
  Row,
  Select,
  Skeleton,
  Spinner,
  Tabs,
  Textarea,
  cn,
  type Column,
} from '../components/ui';
import { api } from '../lib/api';
import {
  formatCredits,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatRate,
  humanize,
  timezoneOffsetMinutes,
} from '../lib/format';
import { useT } from '../lib/i18n';
import { errorMessage, useToast } from '../lib/toast';
import type {
  AudienceFilter,
  DistributionBasis,
  FormulaCheck,
  Paginated,
  PayoutAllocation,
  PayoutRun,
  RewardScheme,
  SchemeVocabulary,
} from '../lib/types';

type EconomyTab = 'runs' | 'schemes';

/**
 * The reward engine.
 *
 * Two halves that only make sense together: the **schemes** decide how a
 * night's forfeits are shared, and the **settlements** are what happened when
 * those rules met a real night. An operator changing a formula wants to see
 * the run it produces; an operator looking at a run wants to know which rule
 * produced it.
 */
export function EconomyPage() {
  const t = useT();
  const [tab, setTab] = useState<EconomyTab>('runs');

  return (
    <>
      <PageHeader
        title={t('economy.title')}
        description={t('economy.description')}
      />

      <Tabs
        label={t('economy.tabsLabel')}
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'runs', label: t('economy.tab.runs') },
          { id: 'schemes', label: t('economy.tab.schemes') },
        ]}
      />

      {tab === 'runs' ? <SettlementsTab /> : <SchemesTab />}
    </>
  );
}

// ── Settlements ─────────────────────────────────────────────────────────────

function SettlementsTab() {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);

  const runs = useQuery({
    queryKey: ['payout-runs', page],
    queryFn: () =>
      api.get<Paginated<PayoutRun>>('/api/payouts/runs', { page, limit: 25 }),
    refetchInterval: 30_000,
  });

  const draft = useMutation({
    mutationFn: () =>
      api.post<PayoutRun>('/api/payouts/runs', {
        tzOffset: timezoneOffsetMinutes(),
      }),
    onSuccess: (run) => {
      toast.success(
        t('economy.runs.drafted', {
          winners: run.winnerCount,
          participants: run.participantCount,
          pool: run.poolAmount,
        }),
      );
      setDrafting(false);
      setSelected(run.id);
      void queryClient.invalidateQueries({ queryKey: ['payout-runs'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const columns: Column<PayoutRun>[] = [
    {
      key: 'window',
      header: t('economy.column.night'),
      render: (run) => (
        <div className="flex flex-wrap items-center gap-2">
          <span>{formatDateTime(run.windowStart)}</span>
          <StatusBadge status={run.status} />
        </div>
      ),
    },
    {
      key: 'wake',
      header: t('economy.column.wokeUp'),
      numeric: true,
      render: (run) => (
        <span>
          {run.winnerCount}/{run.participantCount}
          <span className="ml-1 text-xs text-ink-3">
            ({formatRate(run.wakeRate)})
          </span>
        </span>
      ),
    },
    {
      key: 'pool',
      header: t('economy.column.pool'),
      numeric: true,
      render: (run) => formatNumber(run.poolAmount),
    },
    {
      key: 'commission',
      header: t('economy.column.commission'),
      numeric: true,
      desktopOnly: true,
      render: (run) => formatNumber(run.commissionAmount),
    },
    {
      key: 'paid',
      header: t('economy.column.paid'),
      numeric: true,
      render: (run) => formatNumber(run.paidAmount),
    },
    {
      key: 'trigger',
      header: t('economy.column.startedBy'),
      desktopOnly: true,
      render: (run) => (
        <span className="text-ink-2">{run.actor ?? humanize(run.trigger)}</span>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-4">
        <Card>
          <CardHeader
            title={t('economy.runs.draftTitle')}
            description={t('economy.runs.draftDescription')}
            action={
              <Button
                variant="primary"
                icon={<Play size={15} />}
                onClick={() => setDrafting(true)}
              >
                {t('economy.runs.draftAction')}
              </Button>
            }
          />
        </Card>

        {runs.isError && <ErrorNote error={runs.error} />}

        <Card>
          {runs.isPending ? (
            <Skeleton className="h-80" />
          ) : (
            <>
              <DataTable
                columns={columns}
                rows={runs.data?.items ?? []}
                rowKey={(run) => run.id}
                onRowClick={(run) => setSelected(run.id)}
                empty={
                  <EmptyState
                    icon={<CircleDollarSign size={28} />}
                    title={t('economy.runs.emptyTitle')}
                    description={t('economy.runs.emptyDescription')}
                  />
                }
              />
              <Pagination
                page={runs.data?.page ?? 1}
                totalPages={runs.data?.totalPages ?? 1}
                total={runs.data?.total ?? 0}
                onChange={setPage}
              />
            </>
          )}
        </Card>
      </div>

      <ConfirmDialog
        open={drafting}
        title={t('economy.runs.confirmTitle')}
        description={t('economy.runs.confirmDescription')}
        confirmLabel={t('economy.runs.confirmAction')}
        loading={draft.isPending}
        onCancel={() => setDrafting(false)}
        onConfirm={() => draft.mutate()}
      />

      {selected && (
        <RunDrawer runId={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tones: Record<string, 'neutral' | 'brand' | 'good' | 'warn' | 'danger'> =
    {
      draft: 'warn',
      committing: 'brand',
      committed: 'good',
      cancelled: 'neutral',
      failed: 'danger',
    };
  return <Badge tone={tones[status] ?? 'neutral'}>{humanize(status)}</Badge>;
}

function RunDrawer({ runId, onClose }: { runId: string; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'commit' | 'cancel' | null>(null);
  const [page, setPage] = useState(1);

  const run = useQuery({
    queryKey: ['payout-run', runId],
    queryFn: () => api.get<PayoutRun>(`/api/payouts/runs/${runId}`),
  });

  const allocations = useQuery({
    queryKey: ['payout-allocations', runId, page],
    queryFn: () =>
      api.get<Paginated<PayoutAllocation>>(
        `/api/payouts/runs/${runId}/allocations`,
        { page, limit: 50 },
      ),
  });

  const act = useMutation({
    mutationFn: ({ kind, reason }: { kind: string; reason: string }) =>
      kind === 'commit'
        ? api.post<PayoutRun>(`/api/payouts/runs/${runId}/commit`)
        : api.post<PayoutRun>(`/api/payouts/runs/${runId}/cancel`, { reason }),
    onSuccess: (updated) => {
      toast.success(
        updated.status === 'committed'
          ? t('economy.run.paid', {
              amount: formatCredits(updated.paidAmount),
            })
          : t('economy.run.cancelled'),
      );
      setDialog(null);
      void queryClient.invalidateQueries({ queryKey: ['payout-run', runId] });
      void queryClient.invalidateQueries({ queryKey: ['payout-runs'] });
      void queryClient.invalidateQueries({ queryKey: ['payout-allocations'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const data = run.data;

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        data
          ? t('economy.run.title', { when: formatDateTime(data.windowStart) })
          : t('common.loadingEllipsis')
      }
      footer={
        data?.status === 'draft' && (
          <div className="flex gap-2">
            <Button
              variant="danger"
              className="flex-1"
              icon={<X size={15} />}
              onClick={() => setDialog('cancel')}
            >
              {t('economy.run.discard')}
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              icon={<Check size={15} />}
              onClick={() => setDialog('commit')}
            >
              {t('economy.run.payOut')}
            </Button>
          </div>
        )
      }
    >
      {run.isPending && <Spinner />}
      {run.isError && <ErrorNote error={run.error} />}

      {data && (
        <div className="space-y-5">
          {data.status === 'draft' && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn">
              {t('economy.run.draftNote')}
            </div>
          )}
          {data.error && <ErrorNote error={new Error(data.error)} />}

          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
              {t('economy.run.section.night')}
            </h3>
            <dl className="divide-y divide-line rounded-lg border border-line px-3">
              <Row label={t('economy.run.row.window')}>
                {formatDateTime(data.windowStart)} →{' '}
                {formatDateTime(data.windowEnd)}
              </Row>
              <Row label={t('economy.run.row.settled')}>
                {formatNumber(data.participantCount)}
              </Row>
              <Row label={t('economy.run.row.wokeUp')}>
                {formatNumber(data.winnerCount)} ({formatRate(data.wakeRate)})
              </Row>
              <Row label={t('economy.run.row.missed')}>
                {formatNumber(data.missedCount)}
              </Row>
              <Row label={t('economy.run.row.withheld')}>
                {formatNumber(data.withheldCount)}
              </Row>
            </dl>
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
              {t('economy.run.section.books')}
            </h3>
            <dl className="divide-y divide-line rounded-lg border border-line px-3">
              {data.carriedInAmount > 0 && (
                <Row label={t('economy.run.row.carriedIn')}>
                  {formatCredits(data.carriedInAmount)}
                </Row>
              )}
              <Row label={t('economy.run.row.pool')}>
                {formatCredits(data.poolAmount)}
              </Row>
              <Row label={t('economy.run.row.commission')}>
                {formatCredits(data.commissionAmount)}
              </Row>
              <Row label={t('economy.run.row.paidToUsers')}>
                {formatCredits(data.paidAmount)}
              </Row>
              <Row label={t('economy.run.row.carriedForward')}>
                {formatCredits(data.carryOverAmount)}
              </Row>
            </dl>
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
              {t('economy.run.section.schemes')}
            </h3>
            {data.schemes.length === 0 ? (
              <p className="text-sm text-ink-3">{t('economy.run.noSchemes')}</p>
            ) : (
              <ul className="space-y-2">
                {data.schemes.map((result) => (
                  <li
                    key={result.schemeId}
                    className={cn(
                      'rounded-lg border px-3 py-2.5',
                      result.applied
                        ? 'border-line bg-surface'
                        : 'border-line bg-raised',
                    )}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm font-medium text-ink">
                        {result.schemeName}
                      </span>
                      <Badge tone={result.source === 'pool' ? 'neutral' : 'brand'}>
                        {result.source === 'pool'
                          ? t('economy.run.fromPool')
                          : t('economy.run.minted')}
                      </Badge>
                    </div>
                    {result.applied ? (
                      <p className="mt-1 text-xs text-ink-2">
                        {t('economy.run.schemeApplied', {
                          recipients: result.recipients,
                          paid: formatCredits(result.paid),
                          pool: formatCredits(result.pool),
                        })}
                        {result.commission > 0 &&
                          ` · ${t('economy.run.schemeCommission', {
                            amount: formatCredits(result.commission),
                          })}`}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-ink-3">
                        {t('economy.run.schemeSkipped', {
                          reason: result.reason ?? '',
                        })}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
              {t('economy.run.section.allocations')}
            </h3>
            {allocations.isPending ? (
              <Skeleton className="h-64" />
            ) : (allocations.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-ink-3">
                {t('economy.run.noAllocations')}
              </p>
            ) : (
              <div className="rounded-lg border border-line">
                <ul className="divide-y divide-line">
                  {(allocations.data?.items ?? []).map((allocation) => (
                    <li key={allocation.id} className="px-3 py-2.5">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="tnum shrink-0 text-xs text-ink-3">
                            #{allocation.rank}
                          </span>
                          <IdChip id={allocation.userId} />
                        </span>
                        <span className="tnum shrink-0 text-sm font-semibold text-ink">
                          {formatNumber(allocation.amount)}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-ink-3">
                        {t('economy.run.allocationLine', {
                          scheme: allocation.schemeName,
                          weight: allocation.weight,
                          percent: allocation.sharePercent,
                        })}
                        {allocation.solveMs !== null &&
                          ` · ${t('economy.run.allocationSolved', {
                            duration: formatDuration(allocation.solveMs),
                          })}`}
                        {allocation.streak > 0 &&
                          ` · ${t('economy.run.allocationStreak', {
                            count: allocation.streak,
                          })}`}
                      </p>
                    </li>
                  ))}
                </ul>
                <Pagination
                  page={allocations.data?.page ?? 1}
                  totalPages={allocations.data?.totalPages ?? 1}
                  total={allocations.data?.total ?? 0}
                  onChange={setPage}
                />
              </div>
            )}
          </section>
        </div>
      )}

      <ConfirmDialog
        open={dialog !== null}
        title={
          dialog === 'commit'
            ? t('economy.run.commitTitle', {
                amount: formatCredits(
                  data?.schemes.reduce((sum, s) => sum + s.paid, 0) ?? 0,
                ),
              })
            : t('economy.run.cancelTitle')
        }
        description={t(
          dialog === 'commit'
            ? 'economy.run.commitDescription'
            : 'economy.run.cancelDescription',
        )}
        destructive={dialog === 'cancel'}
        confirmLabel={t(
          dialog === 'commit' ? 'economy.run.payOut' : 'economy.run.discard',
        )}
        loading={act.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) =>
          dialog && act.mutate({ kind: dialog, reason })
        }
      />
    </Drawer>
  );
}

// ── Schemes ─────────────────────────────────────────────────────────────────

function SchemesTab() {
  const t = useT();
  const [editing, setEditing] = useState<RewardScheme | 'new' | null>(null);

  const schemes = useQuery({
    queryKey: ['schemes'],
    queryFn: () => api.get<RewardScheme[]>('/api/payouts/schemes'),
  });

  return (
    <>
      <div className="space-y-4">
        <Card>
          <CardHeader
            title={t('economy.schemes.title')}
            description={t('economy.schemes.description')}
            action={
              <Button
                variant="primary"
                icon={<Plus size={15} />}
                onClick={() => setEditing('new')}
              >
                {t('economy.schemes.new')}
              </Button>
            }
          />
        </Card>

        {schemes.isError && <ErrorNote error={schemes.error} />}

        {schemes.isPending ? (
          <Skeleton className="h-64" />
        ) : (schemes.data?.length ?? 0) === 0 ? (
          <Card>
            <EmptyState
              icon={<CircleDollarSign size={28} />}
              title={t('economy.schemes.emptyTitle')}
              description={t('economy.schemes.emptyDescription')}
              action={
                <Button variant="primary" onClick={() => setEditing('new')}>
                  {t('economy.schemes.emptyAction')}
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {schemes.data?.map((scheme) => (
              <SchemeCard
                key={scheme.id}
                scheme={scheme}
                onEdit={() => setEditing(scheme)}
              />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <SchemeEditor
          scheme={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function SchemeCard({
  scheme,
  onEdit,
}: {
  scheme: RewardScheme;
  onEdit: () => void;
}) {
  const t = useT();

  return (
    <Card>
      <button
        type="button"
        onClick={onEdit}
        className="w-full px-4 py-3.5 text-left transition-colors hover:bg-raised sm:px-5"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-ink">{scheme.name}</span>
            {!scheme.enabled && <Badge>{t('common.disabled')}</Badge>}
            <Badge tone={scheme.source === 'pool' ? 'neutral' : 'brand'}>
              {scheme.source === 'pool'
                ? t('economy.run.fromPool')
                : t('economy.schemes.mintedBonus')}
            </Badge>
            <Badge tone="neutral">
              {t('common.priority')} {scheme.priority}
            </Badge>
          </div>
          <span className="text-xs text-ink-3">
            {t('economy.schemes.commissionPercent', {
              percent: scheme.distribution.commissionPercent,
            })}
          </span>
        </div>

        {scheme.description && (
          <p className="mt-1 text-xs text-ink-2">{scheme.description}</p>
        )}

        <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
          <div className="rounded-lg bg-raised px-2.5 py-1.5">
            <dt className="text-ink-3">{t('economy.schemes.splitsBy')}</dt>
            <dd className="mt-0.5 font-medium text-ink">
              {t(BASIS_KEYS[scheme.distribution.basis])}
              {scheme.distribution.basis === 'custom' &&
                scheme.distribution.weightFormula && (
                  <code className="ml-1 font-mono text-[11px] text-ink-2">
                    {scheme.distribution.weightFormula}
                  </code>
                )}
            </dd>
          </div>
          <div className="rounded-lg bg-raised px-2.5 py-1.5">
            <dt className="text-ink-3">{t('economy.schemes.appliesWhen')}</dt>
            <dd className="mt-0.5 font-medium text-ink">
              {scheme.cycleCondition ? (
                <code className="font-mono text-[11px]">
                  {scheme.cycleCondition}
                </code>
              ) : (
                t('common.always')
              )}
            </dd>
          </div>
        </dl>

        <p className="mt-2">
          <AudienceSummary filter={scheme.audience} />
        </p>
      </button>
    </Card>
  );
}

const BASIS_KEYS: Record<DistributionBasis, string> = {
  equal: 'economy.basis.equal',
  speed: 'economy.basis.speed',
  streak: 'economy.basis.streak',
  stake: 'economy.basis.stake',
  rank: 'economy.basis.rank',
  custom: 'economy.basis.custom',
};

/**
 * The scheme editor.
 *
 * The formula box validates as you type — against the exact variable list the
 * calculator will evaluate it with — and shows what it pays for a sample
 * participant. That live answer is the whole point: a payout rule you cannot
 * try is a payout rule you find out about at four in the morning.
 */
function SchemeEditor({
  scheme,
  onClose,
}: {
  scheme: RewardScheme | null;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'save' | 'delete' | null>(null);

  const [form, setForm] = useState({
    name: scheme?.name ?? '',
    description: scheme?.description ?? '',
    enabled: scheme?.enabled ?? true,
    priority: String(scheme?.priority ?? 100),
    source: scheme?.source ?? 'pool',
    basis: scheme?.distribution.basis ?? 'equal',
    commissionPercent: String(scheme?.distribution.commissionPercent ?? 10),
    weightFormula: scheme?.distribution.weightFormula ?? '',
    minPayout: String(scheme?.distribution.minPayout ?? 1),
    maxSharePercent: String(scheme?.distribution.maxSharePercent ?? 100),
    remainder: scheme?.distribution.remainder ?? 'largest_remainder',
    cycleCondition: scheme?.cycleCondition ?? '',
    participantCondition: scheme?.participantCondition ?? '',
    poolSharePercent: String(scheme?.poolSharePercent ?? 100),
    budget: String(scheme?.budget ?? 0),
    perWinnerAmount: String(scheme?.perWinnerAmount ?? 0),
  });
  const [audience, setAudience] = useState<AudienceFilter>(
    scheme?.audience ?? {},
  );

  const vocabulary = useQuery({
    queryKey: ['scheme-vocabulary'],
    queryFn: () => api.get<SchemeVocabulary>('/api/payouts/schemes/vocabulary'),
    staleTime: Infinity,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const save = useMutation({
    mutationFn: (reason: string) => {
      const body = {
        name: form.name,
        description: form.description || undefined,
        enabled: form.enabled,
        priority: Number(form.priority),
        source: form.source,
        distribution: {
          basis: form.basis,
          commissionPercent: Number(form.commissionPercent),
          weightFormula: form.weightFormula || undefined,
          minPayout: Number(form.minPayout),
          maxSharePercent: Number(form.maxSharePercent),
          remainder: form.remainder,
        },
        cycleCondition: form.cycleCondition || undefined,
        participantCondition: form.participantCondition || undefined,
        audience,
        poolSharePercent: Number(form.poolSharePercent),
        budget: Number(form.budget),
        perWinnerAmount: Number(form.perWinnerAmount),
        reason,
      };

      return scheme
        ? api.patch<RewardScheme>(`/api/payouts/schemes/${scheme.id}`, body)
        : api.post<RewardScheme>('/api/payouts/schemes', body);
    },
    onSuccess: () => {
      toast.success(
        t(scheme ? 'economy.editor.updated' : 'economy.editor.created'),
      );
      void queryClient.invalidateQueries({ queryKey: ['schemes'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (reason: string) =>
      api.delete(`/api/payouts/schemes/${scheme!.id}`, { reason }),
    onSuccess: () => {
      toast.success(t('economy.editor.deleted'));
      void queryClient.invalidateQueries({ queryKey: ['schemes'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        scheme
          ? t('economy.editor.editTitle', { name: scheme.name })
          : t('economy.editor.newTitle')
      }
      footer={
        <div className="flex gap-2">
          {scheme && (
            <Button
              variant="danger"
              icon={<Trash2 size={15} />}
              onClick={() => setDialog('delete')}
            >
              {t('common.delete')}
            </Button>
          )}
          <Button
            variant="primary"
            className="flex-1"
            disabled={!form.name.trim()}
            onClick={() => setDialog('save')}
          >
            {scheme ? t('common.saveChanges') : t('economy.editor.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <section className="space-y-3">
          <Field label={t('common.name')}>
            <Input
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              placeholder={t('economy.editor.namePlaceholder')}
            />
          </Field>
          <Field
            label={t('common.description')}
            hint={t('economy.editor.descriptionHint')}
          >
            <Textarea
              rows={2}
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('common.enabled')}>
              <Select
                value={String(form.enabled)}
                onChange={(event) => set('enabled', event.target.value === 'true')}
              >
                <option value="true">{t('economy.editor.enabledYes')}</option>
                <option value="false">{t('economy.editor.enabledNo')}</option>
              </Select>
            </Field>
            <Field
              label={t('common.priority')}
              hint={t('economy.editor.priorityHint')}
            >
              <Input
                type="number"
                value={form.priority}
                onChange={(event) => set('priority', event.target.value)}
              />
            </Field>
          </div>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('economy.editor.section.source')}
          </h3>
          <Field
            label={t('economy.editor.source')}
            hint={t(
              form.source === 'pool'
                ? 'economy.editor.sourcePoolHint'
                : 'economy.editor.sourceSystemHint',
            )}
          >
            <Select
              value={form.source}
              onChange={(event) =>
                set('source', event.target.value as 'pool' | 'system')
              }
            >
              <option value="pool">{t('economy.editor.sourcePool')}</option>
              <option value="system">{t('economy.editor.sourceSystem')}</option>
            </Select>
          </Field>

          {form.source === 'pool' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('economy.editor.poolShare')}
                hint={t('economy.editor.poolShareHint')}
              >
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={form.poolSharePercent}
                  onChange={(event) =>
                    set('poolSharePercent', event.target.value)
                  }
                />
              </Field>
              <Field
                label={t('economy.editor.commission')}
                hint={t('economy.editor.commissionHint')}
              >
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={form.commissionPercent}
                  onChange={(event) =>
                    set('commissionPercent', event.target.value)
                  }
                />
              </Field>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={t('economy.editor.perWinner')}
                hint={t('economy.editor.perWinnerHint')}
              >
                <Input
                  type="number"
                  min={0}
                  value={form.perWinnerAmount}
                  onChange={(event) =>
                    set('perWinnerAmount', event.target.value)
                  }
                />
              </Field>
              <Field
                label={t('economy.editor.budget')}
                hint={t('economy.editor.budgetHint')}
              >
                <Input
                  type="number"
                  min={0}
                  value={form.budget}
                  onChange={(event) => set('budget', event.target.value)}
                />
              </Field>
            </div>
          )}
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('economy.editor.section.split')}
          </h3>
          <Field label={t('economy.editor.basis')}>
            <Select
              value={form.basis}
              onChange={(event) =>
                set('basis', event.target.value as DistributionBasis)
              }
            >
              {(Object.keys(BASIS_KEYS) as DistributionBasis[]).map((basis) => (
                <option key={basis} value={basis}>
                  {t(BASIS_KEYS[basis])}
                </option>
              ))}
            </Select>
          </Field>

          {form.basis === 'custom' && (
            <FormulaField
              label={t('economy.editor.weightPerWinner')}
              scope="participant"
              value={form.weightFormula}
              onChange={(next) => set('weightFormula', next)}
              vocabulary={vocabulary.data}
              sample={{
                speedScore: 0.8,
                rank: 3,
                streak: 4,
                stake: 10,
                solveSeconds: 6,
                winners: 70,
                participants: 100,
                distributable: 270,
              }}
            />
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('economy.editor.minPayout')}
              hint={t('economy.editor.minPayoutHint')}
            >
              <Input
                type="number"
                min={0}
                value={form.minPayout}
                onChange={(event) => set('minPayout', event.target.value)}
              />
            </Field>
            <Field label={t('economy.editor.maxShare')}>
              <Input
                type="number"
                min={1}
                max={100}
                value={form.maxSharePercent}
                onChange={(event) => set('maxSharePercent', event.target.value)}
              />
            </Field>
          </div>

          <Field
            label={t('economy.editor.remainder')}
            hint={t('economy.editor.remainderHint')}
          >
            <Select
              value={form.remainder}
              onChange={(event) =>
                set(
                  'remainder',
                  event.target.value as typeof form.remainder,
                )
              }
            >
              <option value="largest_remainder">
                {t('economy.editor.remainderLargest')}
              </option>
              <option value="house">{t('economy.editor.remainderHouse')}</option>
              <option value="carry_over">
                {t('economy.editor.remainderCarry')}
              </option>
            </Select>
          </Field>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('economy.editor.section.applies')}
          </h3>
          <FormulaField
            label={t('economy.editor.cycleCondition')}
            scope="cycle"
            value={form.cycleCondition}
            onChange={(next) => set('cycleCondition', next)}
            vocabulary={vocabulary.data}
            placeholder={t('economy.editor.cyclePlaceholder')}
            sample={{
              participants: 100,
              winners: 100,
              missed: 0,
              withheld: 0,
              wakeRate: 1,
              pool: 0,
              commission: 0,
              distributable: 0,
              allocated: 0,
            }}
          />
          <FormulaField
            label={t('economy.editor.participantCondition')}
            scope="participant"
            value={form.participantCondition}
            onChange={(next) => set('participantCondition', next)}
            vocabulary={vocabulary.data}
            placeholder={t('economy.editor.participantPlaceholder')}
            sample={{ streak: 4, rank: 3, speedScore: 0.8, stake: 10 }}
          />
        </section>

        <section className="space-y-2 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('economy.editor.section.eligible')}
          </h3>
          <p className="text-xs text-ink-3">
            {t('economy.editor.eligibleNote')}
          </p>
          <AudienceFilters
            value={audience}
            onChange={setAudience}
            showSearch={false}
          />
        </section>
      </div>

      <ConfirmDialog
        open={dialog !== null}
        title={
          dialog === 'delete'
            ? t('economy.editor.deleteTitle', { name: scheme?.name ?? '' })
            : scheme
              ? t('common.saveTheseChanges')
              : t('economy.editor.createTitle')
        }
        description={t(
          dialog === 'delete'
            ? 'economy.editor.deleteDescription'
            : 'economy.editor.saveDescription',
        )}
        destructive={dialog === 'delete'}
        loading={save.isPending || remove.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) =>
          dialog === 'delete' ? remove.mutate(reason) : save.mutate(reason)
        }
      />
    </Drawer>
  );
}

/**
 * A formula box that answers back.
 *
 * It asks the backend to compile the expression — using the same compiler the
 * settlement will — and evaluates it against a plausible sample, so an
 * operator sees a number rather than a hope. A typo is a red line here instead
 * of a payout that silently weighs everybody at zero.
 */
function FormulaField({
  label,
  scope,
  value,
  onChange,
  vocabulary,
  sample,
  placeholder,
}: {
  label: string;
  scope: 'participant' | 'cycle';
  value: string;
  onChange: (value: string) => void;
  vocabulary: SchemeVocabulary | undefined;
  sample: Record<string, number>;
  placeholder?: string;
}) {
  const t = useT();
  const [check, setCheck] = useState<FormulaCheck | null>(null);

  // Debounced, because it fires per keystroke and each one is a round trip.
  const trimmed = value.trim();
  useEffect(() => {
    if (!trimmed) {
      setCheck(null);
      return;
    }

    const timer = setTimeout(() => {
      void api
        .post<FormulaCheck>('/api/payouts/schemes/validate', {
          formula: trimmed,
          scope,
          sample,
        })
        .then(setCheck)
        .catch(() => setCheck(null));
    }, 350);

    return () => clearTimeout(timer);
    // `sample` is a literal at every call site, so it is stable in practice;
    // including it would re-run the check on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, scope]);

  const metrics = useMemo(
    () =>
      scope === 'cycle'
        ? (vocabulary?.cycleMetrics ?? [])
        : (vocabulary?.participantMetrics ?? []),
    [scope, vocabulary],
  );

  return (
    <div className="space-y-2">
      <Field label={label}>
        <Input
          className="font-mono text-xs"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder ?? t('economy.formula.placeholder')}
        />
      </Field>

      {check && (
        <p
          className={cn(
            'rounded-lg px-2.5 py-1.5 text-xs',
            check.valid
              ? 'bg-good-soft text-good'
              : 'bg-danger-soft text-danger',
          )}
        >
          {check.valid
            ? t('economy.formula.valid', { result: String(check.result) })
            : check.error}
        </p>
      )}

      {scope === 'participant' && vocabulary && (
        <details className="text-xs">
          <summary className="cursor-pointer text-ink-2">
            {t('economy.formula.variables')}
          </summary>
          <div className="mt-2 space-y-2">
            <p className="text-ink-3 break-anywhere">
              <span className="font-medium text-ink-2">
                {t('common.available')}
              </span>{' '}
              <code className="font-mono">{metrics.join(', ')}</code>
            </p>
            <ul className="space-y-1.5">
              {vocabulary.examples.map((example) => (
                <li key={example.formula}>
                  <button
                    type="button"
                    onClick={() => onChange(example.formula)}
                    className="w-full rounded-lg bg-raised px-2.5 py-1.5 text-left transition-colors hover:bg-line"
                  >
                    <code className="font-mono text-[11px] text-ink">
                      {example.formula}
                    </code>
                    <p className="mt-0.5 text-ink-3">{example.explanation}</p>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}

      {scope === 'cycle' && vocabulary && (
        <p className="text-xs text-ink-3 break-anywhere">
          <span className="font-medium text-ink-2">
            {t('common.available')}
          </span>{' '}
          <code className="font-mono">{metrics.join(', ')}</code>
        </p>
      )}
    </div>
  );
}
